import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { StatCards, TabSkeleton } from '@/components/panel/panels';
import { ScopePills } from '@/components/table/ScopePills';
import { useAuth } from '@/lib/auth/auth-context';
import { TabError } from '@/screens/user-detail/TabError';

import { FunnelPanel } from './FunnelPanel';
import { HealthPanel } from './HealthPanel';
import { PlanHealthPanel, type PlanHealthScope } from './PlanHealthPanel';
import { SpendPanel } from './SpendPanel';
import { overviewQueryOptions } from './overview-query';
import { OVERVIEW_WINDOW_OPTIONS, type OverviewSearch } from './overview-search';

export interface OverviewScreenProps {
  readonly search: OverviewSearch;
  readonly onSearchChange: (next: OverviewSearch) => void;
  /** Opens the list a plan-health figure is counted from. */
  readonly onOpenPlanScope: (scope: PlanHealthScope) => void;
}

/**
 * Four questions, in the order they get asked: how big is this, where do
 * people stop, what are the models costing, and is anything broken.
 *
 * Deliberately no second copy of the counts further down. An earlier version
 * repeated the KPI numbers as a field grid and the plan figures as another,
 * which meant two places to read the same thing and two places for them to
 * disagree while one of them was still loading.
 */
export function OverviewScreen({ search, onSearchChange, onOpenPlanScope }: OverviewScreenProps) {
  const { api } = useAuth();
  const query = useQuery(overviewQueryOptions(api, search.days));

  const onSelectWindow = useCallback(
    (days: number) => {
      onSearchChange({ days });
    },
    [onSearchChange],
  );

  const overview = query.data;

  return (
    <div className="flex max-w-[1240px] flex-col gap-3.5">
      {/* The window pills lead, as they do on AI spend: every figure below is
          drawn over the window they set. */}
      <ScopePills
        label="Window"
        scopes={OVERVIEW_WINDOW_OPTIONS.map((days) => ({ id: String(days), label: `${String(days)}d` }))}
        value={String(search.days)}
        onChange={(days) => {
          onSelectWindow(Number(days));
        }}
      />

      {query.isPending ? <TabSkeleton rows={4} /> : null}
      {query.isError ? <TabError message={query.error.message} onRetry={() => void query.refetch()} /> : null}

      {overview === undefined ? null : (
        <>
          <StatCards
            stats={[
              {
                label: 'Accounts',
                value: overview.users.total.toLocaleString('en-IN'),
                note: `${overview.users.active.toLocaleString('en-IN')} active, ${overview.users.deactivated.toLocaleString('en-IN')} deactivated`,
              },
              {
                label: 'Finished onboarding',
                value:
                  overview.users.total === 0
                    ? '—'
                    : `${String(Math.round((overview.users.onboarded / overview.users.total) * 100))}%`,
                note: `${overview.users.onboarded.toLocaleString('en-IN')} of ${overview.users.total.toLocaleString('en-IN')} accounts`,
              },
              {
                label: 'Premium',
                value: overview.billing.premium.toLocaleString('en-IN'),
                note: `${String(overview.billing.trialing)} trialing, ${String(overview.billing.billingIssue)} with a billing issue`,
              },
              {
                label: 'iPhone users',
                value: overview.users.byPlatform.ios.toLocaleString('en-IN'),
                note: `${overview.users.byPlatform.android.toLocaleString('en-IN')} Android, ${overview.users.byPlatform.web.toLocaleString('en-IN')} web, ${overview.users.byPlatform.none.toLocaleString('en-IN')} no device yet`,
              },
              {
                label: `New in ${String(overview.windowDays)} days`,
                value: `+${overview.users.newInWindow.toLocaleString('en-IN')}`,
                note: `${overview.scans.inWindow.toLocaleString('en-IN')} food scans over the same window`,
              },
            ]}
          />

          <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-[1fr_1.25fr]">
            <FunnelPanel overview={overview} />
            <SpendPanel overview={overview} />
          </div>

          <div className="grid grid-cols-1 items-start gap-3 xl:grid-cols-[1.25fr_1fr]">
            <PlanHealthPanel overview={overview} onOpen={onOpenPlanScope} />
            <HealthPanel overview={overview} />
          </div>
        </>
      )}
    </div>
  );
}
